
import React, { useEffect, useRef, useState } from 'react';
import { RootState } from '@/store';
import { VenueOnboardingProgress, ProgressStep } from '@/components/turf/VenueOnboardingProgress';
import BasicTurfInfoForm from '@/components/turf/BasicTurfInfoForm';
import TurfAddressForm from '@/components/turf/TurfAddressForm';
import SportsCategoriesForm from '@/components/turf/SportsCategoriesForm';
import SportsManagementForm from '@/components/turf/SportsManagementForm';
import VenueImagesForm from '@/components/turf/VenueImagesForm';

import VendorOnboardSkeleton from '@/components/skeletons/VendorOnboardSkeleton';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'
import { getVendorVenueDetailsById } from '@/store/thunkMidddleware/turfMiddleware';
import { resetVenueDeatils } from '@/store/slices/turfSlice';
import routes from '@/routes/routes.json';

import { useNavigate, useParams } from "react-router-dom";

type OnboardingStep = 'basic' | 'address' | 'categories' | 'sports' | 'images';

const PROGRESS_STEP: Record<OnboardingStep, ProgressStep> = {
  basic: 'turf-basic',
  address: 'turf-address',
  categories: 'turf-categories',
  sports: 'turf-sports',
  images: 'turf-images',
};

// The API reports progress as a percentage rather than a step name, so this is
// the one place that translates the two vocabularies.
const stepForPercentage = (percentage: number): OnboardingStep => {
  if (percentage >= 75) return 'sports';
  if (percentage >= 50) return 'categories';
  if (percentage >= 25) return 'address';
  return 'basic';
};

const VenueOnboard: React.FC = () => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const navigate = useNavigate()
  const { pendingVenueDetails, currentOnboardVenueDetails } = useAppSelector((state: RootState) => state.turf);
  const [step, setStep] = useState<OnboardingStep>('basic');
  const restoredForVenueId = useRef<string | null>(null);

  // Drop the vendor back where they left off the first time a venue's details
  // land. The sports step refetches the venue on nearly every interaction, so
  // this deliberately fires once per venue — otherwise a refetch part-way
  // through would drag them back to an earlier step.
  useEffect(() => {
    const venue = currentOnboardVenueDetails?.payload;
    if (!venue?._id || restoredForVenueId.current === venue._id) return;
    restoredForVenueId.current = venue._id;
    setStep(stepForPercentage(venue.venueOnBoardingPercentage));
  }, [currentOnboardVenueDetails?.payload])

  useEffect(() => {
    if (params?.id) {
      dispatch(getVendorVenueDetailsById({
        pathparams: params?.id, callback: (res) => {
          if (res?.error === "Venue not found") {
            navigate(routes.venueList)
          }
        }
      }))
    } else {
      restoredForVenueId.current = null;
      dispatch(resetVenueDeatils(null))
      setStep('basic')
    }
  }, [params?.id])
  const renderCurrentStep = () => {
    switch (step) {
      case 'basic':
        return <BasicTurfInfoForm onNext={() => setStep('address')} />;

      case 'address':
        return (
          <TurfAddressForm
            onNext={() => setStep('categories')}
            onBack={() => setStep('basic')}
          />
        );

      case 'categories':
        return (
          <SportsCategoriesForm
            onNext={() => setStep('sports')}
            onBack={() => setStep('address')}
          />
        );

      case 'sports':
        return (
          <SportsManagementForm
            onNext={() => setStep('images')}
            onBack={() => setStep('categories')}
          />
        );

      case 'images':
        return (
          <VenueImagesForm
            // The images step is the last one — it navigates to the venue list
            // itself once the venue is complete, so there is no next step.
            onNext={() => {}}
            onBack={() => setStep('sports')}
          />
        );
      default:
        return null;
    }
  };

  if (pendingVenueDetails.isLoading) {
    return <VendorOnboardSkeleton />
  }

  return (
    // The progress rail sticks beside the form on desktop so a vendor can see
    // how much of the onboarding is left without scrolling back up to find out.
    <div className="flex flex-col lg:flex-row gap-4">
      <VenueOnboardingProgress currentStep={PROGRESS_STEP[step]} />
      <div className="flex-1 min-w-0">
        <div className="w-full max-w-4xl">{renderCurrentStep()}</div>
      </div>
    </div>
  );
};

export default VenueOnboard;
